import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from './env';
import { useAuthStore } from '@/stores/auth.store';
import type { AuthTokens, RefreshResponse } from '@/types/auth';
import type { ApiEnvelope } from '@/types/api';

export const http: AxiosInstance = axios.create({
  baseURL: env.API_URL,
  headers: { 'Content-Type': 'application/json' },
  // Required for the refresh-token cookie. identity-service sets it httpOnly on
  // /auth/login, and a cross-origin response's Set-Cookie is DISCARDED by the
  // browser unless the request carried credentials — so without this the cookie
  // was never stored, and /auth/refresh had nothing to authenticate with.
  withCredentials: true,
});

// ─── Request: inject Bearer token ──────────────────────────────────────────
http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Response: single-flight refresh-on-401 ────────────────────────────────
let refreshPromise: Promise<string | null> | null = null;

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/** Refresh once; concurrent 401s share the same in-flight promise. */
async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  const { refreshToken, setTokens, logout } = useAuthStore.getState();

  // No early `if (!refreshToken) logout()`. Against identity-service the app
  // never holds a refresh token — it is an httpOnly cookie — so that guard
  // logged the cashier out on the FIRST 401 instead of refreshing, ending the
  // shift mid-sale. The cookie is sent by the browser either way; the body
  // field is only for the monolith, which expects it there.
  refreshPromise = (async () => {
    try {
      // Bare axios — bypasses interceptors to avoid a refresh loop.
      const res = await axios.post<ApiEnvelope<RefreshResponse>>(
        `${env.API_URL}/auth/refresh`,
        refreshToken ? { refreshToken } : {},
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
      );
      const body = res.data;
      // Accept all three response shapes, as `unwrap` does: the monolith's
      // `{status,data}`, the services' `{success,data}`, and identity-service's
      // bare `{accessToken, expiresIn}`. Only `status` was checked before, so a
      // successful services refresh was treated as a rejection and logged out.
      if (body?.status === false || (body as { success?: boolean })?.success === false) {
        throw new Error('refresh rejected');
      }
      const d = (body?.status === true || (body as { success?: boolean })?.success === true
        ? body.data
        : (body as unknown as RefreshResponse)) ?? ({} as RefreshResponse);
      const tokens: AuthTokens = d.tokens ?? {
        accessToken: d.accessToken ?? '',
        // `?? ''`, because the store's refreshToken is `string | null` while
        // AuthTokens.refreshToken is `string`. Empty is the honest value here:
        // against identity-service the app never holds one at all — it is an
        // httpOnly cookie — and `refreshAccessToken` already treats empty as
        // "send `{}` and let the browser replay the cookie".
        refreshToken: d.refreshToken ?? refreshToken ?? '',
        expiresIn: d.expiresIn,
      };
      if (!tokens.accessToken) throw new Error('no access token in refresh');
      setTokens(tokens);
      return tokens.accessToken;
    } catch {
      logout();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isRefreshCall = original?.url?.includes('/auth/refresh');

    if (status === 401 && original && !original._retry && !isRefreshCall) {
      original._retry = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return http(original);
      }
      // Refresh failed — fall through; the app's RequireAuth bounces to /login.
    }

    return Promise.reject(error);
  },
);
